import React, { useState, useRef, useEffect } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { MarkerTimeline } from './MarkerTimeline';
import { MarkerCard } from './MarkerCard';
import { Button } from '@/components/ui/button';
import { Play, Pause, SkipBack, SkipForward, Scissors, Check } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

export function MarkerReviewScreen() {
    const { currentProject, markers, updateMarker } = useProjectStore();
    const [currentTime, setCurrentTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);

    const videoRef = useRef<HTMLVideoElement>(null);

    // Find pending markers
    const pendingMarkers = markers.filter(m => m.status === 'pending');
    const activeMarker = activeMarkerId
        ? markers.find(m => m.id === activeMarkerId)
        : pendingMarkers[0];

    useEffect(() => {
        setActiveMarkerId(pendingMarkers[0]?.id || null);
    }, [markers]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);

            // Auto-scroll to active marker if playing? 
            // Or auto-select marker based on time?
            // Simple logic: if inside a marker, activate it
            const insideMarker = pendingMarkers.find(
                m => currentTime >= m.timestampStart && currentTime <= m.timestampEnd
            );
            if (insideMarker && insideMarker.id !== activeMarkerId) {
                setActiveMarkerId(insideMarker.id);
            }
        }
    };

    const jumpToMarker = (id: string) => {
        const marker = markers.find(m => m.id === id);
        if (marker && videoRef.current) {
            // Jump a bit before start for context
            const time = Math.max(0, marker.timestampStart - 2);
            videoRef.current.currentTime = time;
            setCurrentTime(time);
            setActiveMarkerId(id);
            if (!isPlaying) togglePlay();
        }
    };

    const handleApprove = (id: string) => {
        updateMarker(id, { status: 'approved' });
        // Auto-advance
        const next = pendingMarkers[1]; // Since current [0] is now resolved (optimistically)
        if (next) setActiveMarkerId(next.id);
    };

    const handleReject = (id: string) => {
        updateMarker(id, { status: 'rejected' });
        // Auto-advance
        const next = pendingMarkers[1];
        if (next) setActiveMarkerId(next.id);
    };

    // Export Logic
    const navigate = useNavigate();
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        if (!currentProject) return;
        setIsExporting(true);
        try {
            const API_URL = import.meta.env.VITE_API_URL || 'https://vidmastro.com/api';
            await axios.post(`${API_URL}/projects/${currentProject.id}/export`, {
                settings: {
                    quality: '1080p',
                    platform: 'all'
                }
            });

            // Navigate to project detail to see processing status
            navigate(`/projects/${currentProject.id}`);

        } catch (error) {
            console.error("Export failed", error);
            alert("Failed to start export. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    if (!currentProject) {
        return <div className="p-8 text-center">Select a project to review</div>;
    }

    // Use a placeholder or R2 URL
    const videoUrl = currentProject.originalVideoUrl || "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4"; // Fallback for dev

    return (
        <div className="flex flex-col h-[calc(100vh-60px)] max-w-md mx-auto bg-background">
            {/* 1. Video Player (35%) */}
            <div className="relative w-full aspect-video bg-black shrink-0">
                <video
                    ref={videoRef}
                    src={videoUrl}
                    className="w-full h-full object-contain"
                    onTimeUpdate={handleTimeUpdate}
                    playsInline
                    onClick={togglePlay}
                />

                {/* Simple Controls Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {!isPlaying && <div className="bg-black/40 p-4 rounded-full"><Play className="fill-white text-white w-8 h-8" /></div>}
                </div>
            </div>

            {/* 2. Timeline (20%) */}
            <div className="p-2 shrink-0 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                <div className="flex justify-between items-center mb-1 text-xs text-muted-foreground px-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{currentProject.originalDuration ? formatTime(currentProject.originalDuration) : '--:--'}</span>
                </div>
                <MarkerTimeline
                    audioUrl={videoUrl} // Wavesurfer handles extracting audio from video URL usually, or we pass separate audio URL
                    markers={markers}
                    currentTime={currentTime}
                    onTimeUpdate={(t) => {
                        if (videoRef.current) videoRef.current.currentTime = t;
                    }}
                    onMarkerClick={jumpToMarker}
                    activeMarkerId={activeMarkerId}
                    className="h-20"
                />
            </div>

            {/* 3. Marker Cards (45%) - Scrollable List or Stack */}
            <div className="flex-1 overflow-y-auto p-4 bg-muted/30">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-semibold text-lg flex items-center">
                        Review Edits
                        <span className="ml-2 bg-primary/10 text-primary text-xs px-2 py-0.5 rounded-full">
                            {pendingMarkers.length} left
                        </span>
                    </h2>
                    <Button size="sm" variant="ghost">Approve All</Button>
                </div>

                <div className="space-y-4 pb-20">
                    {activeMarker ? (
                        <>
                            <div className="text-sm font-medium text-muted-foreground mb-2">Current Issue</div>
                            <MarkerCard
                                marker={activeMarker}
                                onApprove={handleApprove}
                                onReject={handleReject}
                                onPreview={jumpToMarker}
                                isActive={true}
                            />

                            {pendingMarkers.length > 1 && (
                                <>
                                    <div className="text-sm font-medium text-muted-foreground mt-6 mb-2">Up Next</div>
                                    <div className="opacity-60 pointer-events-none scale-95 origin-top">
                                        <MarkerCard
                                            marker={pendingMarkers[1]}
                                            onApprove={() => { }}
                                            onReject={() => { }}
                                            onPreview={() => { }}
                                            isActive={false}
                                        />
                                    </div>
                                </>
                            )}
                        </>
                    ) : (
                        <div className="text-center py-10 space-y-4">
                            <div className="bg-green-100 dark:bg-green-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
                                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">All caught up!</h3>
                                <p className="text-muted-foreground">You've reviewed all detected markers.</p>
                            </div>
                            <Button
                                size="lg"
                                className="w-full"
                                onClick={handleExport}
                                disabled={isExporting}
                            >
                                {isExporting ? (
                                    <>Processing...</>
                                ) : (
                                    <>
                                        <Scissors className="w-4 h-4 mr-2" />
                                        Apply Edits & Export
                                    </>
                                )}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
}
